import AppContext from "PFApp/app_context";
import { useIsShortlistingPermitted } from "PFCore/helpers/shortlists";
import { useTemplate } from "PFCore/helpers/templates";
import { useActivityRankedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_ranked_shortlists";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { useActivitySuspendedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_suspended_shortlists";
import { useShortlistsInvalidate } from "PFCore/hooks/queries/shortlists/use_shortlists_invalidate";
import { Activity, Shortlist } from "PFTypes";
import { QueryParams } from "PFTypes/query_params";
import { useCallback, useContext, useEffect, useState } from "react";

const useActivityPageShortlists = (activity: Activity) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);

  const isShortlistingPermitted = useIsShortlistingPermitted();

  const { id, template_id, reviewers } = activity;
  const template = useTemplate(template_id);

  const isRole = ["role", "audit_role"].includes(template?.key || "");
  const needMatches = !!template?.need_matches;
  const isReviewer = !!reviewers?.find(({ profile_id }) => profile_id === currentProfile.id);
  const canSeeShortlist = ((needMatches && isShortlistingPermitted(activity)) || isReviewer) && isRole;

  const [params, setParams] = useState<QueryParams>({});

  const shortlistsQueryOptions = {
    enabled: canSeeShortlist,
    keepPreviousData: true
  };

  const { data: shortlistsData, isFetching: isLoadingShortlists } = useActivityShortlists(
    id,
    params,
    shortlistsQueryOptions
  );

  const { data: suspendedShortlistsData, isFetching: isLoadingSuspendedShortlists } =
    useActivitySuspendedShortlists(id, params, shortlistsQueryOptions);

  const isAtLeastOneShortlist =
    !!shortlistsData?.entries?.length || !!suspendedShortlistsData?.entries?.length;

  const { data: rankedShortlistsData, refetch: refetchRankedShortlists } = useActivityRankedShortlists({
    activityId: id,
    options: {
      enabled: canSeeShortlist && isAtLeastOneShortlist
    }
  });

  const { invalidateShortlists } = useShortlistsInvalidate();

  useEffect(() => {
    if (canSeeShortlist) {
      invalidateShortlists(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeShortlist, params]);

  useEffect(() => {
    if (canSeeShortlist && isAtLeastOneShortlist) {
      refetchRankedShortlists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, shortlistsData, suspendedShortlistsData, isAtLeastOneShortlist]);

  const setShortlistsParams = useCallback((params: QueryParams) => {
    setParams((prev) => ({ ...prev, ...params }));
  }, []);

  const rankedShortlistsReady = rankedShortlistsData?.meta?.status === "ready";

  return {
    shortlists: (shortlistsData?.entries as Shortlist[]) || [],
    shortlistsMeta: shortlistsData?.meta || { total: 0 },
    suspendedShortlists: (suspendedShortlistsData?.entries as Shortlist[]) || [],
    suspendedShortlistsMeta: suspendedShortlistsData?.meta || { total: 0 },
    isLoadingShortlists: isLoadingShortlists || isLoadingSuspendedShortlists,
    rankedShortlists: rankedShortlistsReady ? rankedShortlistsData.entries : [],
    setShortlistsParams
  };
};

export default useActivityPageShortlists;
